// =============================================================
// VARIANCE — The Executive Command Center
// File      : Companies.tsx
// Location  : /home/Applications/variance/web/src/pages/Companies.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Displays a list of Companies and provides a form
//             to create a new Company via the API.
// =============================================================

import { useState, useEffect } from 'react';
import { Plus, Building2 } from 'lucide-react';

interface Company {
  id: string;
  code: string;
  name: string;
  industry: string | null;
  pillar1_label: string;
  base_currency: string;
}

export function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    industry: 'real_estate',
    pillar1_label: 'Assets',
    base_currency: 'INR'
  });
  const [formError, setFormError] = useState('');

  // Fetch companies on load
  const fetchCompanies = () => {
    setIsLoading(true);
    fetch('/api/companies')
      .then(res => {
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          return res.json();
        } else {
          throw new Error("API did not return JSON. Check Nginx routing.");
        }
      })
      .then(data => {
        if (!data.error) setCompanies(data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Fetch error:", err);
        setIsLoading(false); // Stop loading so the page doesn't blank out
      });
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Handle Form Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    try {
      const res = await fetch('/api/companies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create company');
      }

      // Success: Close modal, reset form, refresh list
      setIsModalOpen(false);
      setFormData({ code: '', name: '', industry: 'real_estate', pillar1_label: 'Assets', base_currency: 'INR' });
      fetchCompanies();
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Companies</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage your group's operating businesses.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-opacity hover:opacity-90"
          style={{ backgroundColor: 'var(--brand-accent)' }}
        >
          <Plus size={18} /> Add Company
        </button>
      </div>

      {/* Companies Table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Code</th>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Company Name</th>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Industry</th>
              <th className="p-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Currency</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center" style={{ color: 'var(--text-secondary)' }}>Loading...</td></tr>
            ) : companies.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center" style={{ color: 'var(--text-secondary)' }}>No companies found. Add one to get started.</td></tr>
            ) : (
              companies.map(c => (
                <tr key={c.id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 font-mono text-sm" style={{ color: 'var(--brand-accent)' }}>{c.code}</td>
                  <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{c.name}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{c.industry || '-'}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{c.base_currency}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Company Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="w-full max-w-md p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Building2 style={{ color: 'var(--brand-accent)' }} />
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Add New Company</h2>
            </div>

            {formError && (
              <div className="p-3 mb-4 rounded text-sm text-red-500 bg-red-500/10 border border-red-500/20">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Company Code</label>
                <input 
                  required type="text" placeholder="e.g. ALPHA"
                  value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-accent)]"
                />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Company Name</label>
                <input 
                  required type="text" placeholder="e.g. Alpha Developers"
                  value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-accent)]"
                />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Industry</label>
                <select 
                  value={formData.industry} onChange={e => setFormData({...formData, industry: e.target.value})}
                  className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-accent)]"
                >
                  <option value="real_estate">Real Estate</option>
                  <option value="industrial">Industrial / Factory</option>
                  <option value="consulting">Consulting</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Pillar 1 Label</label>
                <input 
                  type="text" placeholder="e.g. Land Bank or Raw Material"
                  value={formData.pillar1_label} onChange={e => setFormData({...formData, pillar1_label: e.target.value})}
                  className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--brand-accent)]"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button 
                  type="button" onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-2 rounded font-medium bg-[var(--bg-panel-alt)] text-[var(--text-primary)] hover:opacity-80"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 py-2 rounded font-medium text-white hover:opacity-90"
                  style={{ backgroundColor: 'var(--brand-accent)' }}
                >
                  Save Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
